import React, { useCallback, useMemo, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { toast } from "react-toastify";
import MediaLibraryModal from "../../MediaLibrary/MediaLibraryModal";
import CropperModal from "../../MediaLibrary/CropperModal";
import useExternalImportCrop from "../../MediaLibrary/useExternalImportCrop";
import {
  TINYMCE_SCRIPT_SRC,
  createTinymceInit,
} from "./tinymceConfig";

const escapeAttr = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const resolveMediaUrl = (media) => {
  if (!media) return "";
  return String(
    media.mediaUrl || media.previewUrl || media.thumbnailUrl || ""
  ).trim();
};

const isVideoMedia = (media, url) => {
  const type = String(media?.mediaType || media?.type || "")
    .toLowerCase()
    .trim();
  if (type.includes("video")) return true;
  return /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(url);
};

/**
 * Shared TinyMCE editor with Media Library image/video insertion.
 * Drop-in replacement for @tinymce/tinymce-react Editor usages.
 */
const RichTextEditor = ({
  value = "",
  onEditorChange,
  height = 450,
  defaultKeyword = "",
  disabled = false,
  id,
}) => {
  const editorRef = useRef(null);
  const filePickerCallbackRef = useRef(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState("image");

  const closeLibrary = useCallback(() => {
    setLibraryOpen(false);
    filePickerCallbackRef.current = null;
  }, []);

  const openImageLibrary = useCallback(() => {
    filePickerCallbackRef.current = null;
    setPickerMode("image");
    setLibraryOpen(true);
  }, []);

  const openLibraryForFilePicker = useCallback((callback, meta) => {
    filePickerCallbackRef.current = callback;
    setPickerMode(meta?.filetype === "media" ? "media" : "image");
    setLibraryOpen(true);
  }, []);

  const insertIntoEditor = useCallback((media) => {
    const editor = editorRef.current;
    const url = resolveMediaUrl(media);
    if (!url) {
      toast.error("Selected media has no usable URL.");
      return;
    }

    if (typeof filePickerCallbackRef.current === "function") {
      try {
        filePickerCallbackRef.current(url, {
          alt: media?.mediaName || "",
          title: media?.mediaName || "",
        });
      } catch {
        toast.error("Unable to insert media into the editor.");
      }
      filePickerCallbackRef.current = null;
      setLibraryOpen(false);
      return;
    }

    if (!editor) {
      toast.error("Editor is not ready. Please try again.");
      return;
    }

    const alt = escapeAttr(media?.mediaName || "");
    const safeUrl = escapeAttr(url);

    try {
      if (pickerMode === "media" || isVideoMedia(media, url)) {
        editor.insertContent(
          `<video controls preload="metadata" src="${safeUrl}" style="max-width:100%;height:auto;"></video>`
        );
      } else {
        editor.insertContent(
          `<img src="${safeUrl}" alt="${alt}" style="max-width:100%;height:auto;" />`
        );
      }
    } catch {
      toast.error("Unable to insert media into the editor.");
    }

    setLibraryOpen(false);
  }, [pickerMode]);

  const handleSelect = useCallback(
    (media) => {
      if (!media) {
        closeLibrary();
        return;
      }
      insertIntoEditor(media);
    },
    [closeLibrary, insertIntoEditor]
  );

  const {
    cropSession,
    applyingCrop,
    handleImportForCrop: startCropSession,
    handleCropCancel,
    handleCropComplete,
    isCropOpen,
  } = useExternalImportCrop(handleSelect);

  const handleImportForCrop = useCallback(
    (session) => {
      setLibraryOpen(false);
      startCropSession(session);
    },
    [startCropSession]
  );

  const init = useMemo(
    () =>
      createTinymceInit({
        height,
        onInsertImage: openImageLibrary,
        filePickerCallback: (callback, _value, meta) => {
          openLibraryForFilePicker(callback, meta);
        },
      }),
    [height, openImageLibrary, openLibraryForFilePicker]
  );

  return (
    <>
      <Editor
        id={id}
        tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
        value={value}
        init={init}
        disabled={disabled}
        onEditorChange={onEditorChange}
        onInit={(_evt, editor) => {
          editorRef.current = editor;
        }}
      />

      <MediaLibraryModal
        show={libraryOpen && !isCropOpen}
        onClose={closeLibrary}
        onSelect={handleSelect}
        onImportForCrop={handleImportForCrop}
        defaultKeyword={defaultKeyword}
        title={
          pickerMode === "media"
            ? "Select Media"
            : "Select Image"
        }
        subtitle={
          pickerMode === "media"
            ? "Choose a video or media asset from the Media Library"
            : "Choose an image from the Media Library"
        }
      />

      <CropperModal
        show={isCropOpen}
        image={cropSession?.cropImage ?? ""}
        fileName={cropSession?.cropFileName ?? "image.png"}
        subtitle="Adjust the crop area, then apply your selection"
        applyLabel="Apply"
        applying={applyingCrop}
        onCancel={handleCropCancel}
        onCropComplete={handleCropComplete}
      />
    </>
  );
};

export default RichTextEditor;
